import { createContext } from 'react';

export type MetroContextType = {
  appName: string;
  clientId: string;
  componentName: string;
  customHandler: ((metricNames: string[], videoId?: string | null) => void) | null;
  subscriptionId: string;
  subscriptionStatus: string;
  userName: string;
  [key: string]: unknown;
};

/**
 * Provides a shareable context for Metro calls for logging and metrics. See {@link context} for
 * a description of the object.
 */

/**
 * The default values for an unset metro context object.
 */
export const metroContextDefaultProperties: Readonly<MetroContextType> = Object.freeze({
  appName: 'modulelibrary',
  clientId: 'unknown',
  componentName: 'unknown',
  customHandler: null,
  subscriptionId: 'unknown',
  subscriptionStatus: 'unknown',
  userName: 'unknown',
});

/**
 * The shared metro context object.
 *
 * The object provided via `<MetroContext.Provider value={value}>` MUST BE STABLE. If used in a
 * class component, store it as an instance value (or in your state/props) in the ctor, or use
 * `useMemo` in your functional component to allow the object to be stable. If you attempt to do
 * `value={{ appName: 'myApp' }}` this will cause your components to re-render constantly as the
 * value is constantly changing.
 *
 * ```javascript
 * import React, { useContext, useMemo } from 'react';
 * import { MetroContext } from '...';
 *
 * export default function MyApp() {
 *   const inheritedMetroContext = useContext(MetroContext);
 *   const metroContext = useMemo(() => ({
 *     ...inheritedMetroContext,
 *     appName: 'myApp',
 *     clientId: user.clientId,
 *   }));
 *
 *   return (
 *     <MetroContext.Provider value={metroContext}>
 *       <MyComponent />
 *     </MetroContext.Provider>
 *   );
 * }
 * ```
 */
const context = createContext({ ...metroContextDefaultProperties });

// provide protection around tests that mock react.
if (context) {
  context.displayName = 'MetroContext';
}

export default context;

/**
 * Sets the value of the property on the object if the property's current value
 * is the default value for metro for that property name.
 * @param obj - The object to inspect and update.
 * @param prop - The property name to examine.
 * @param value - The value to set if the existing property is the default value.
 */
export function setMetroValueIfDefault<
  K extends keyof MetroContextType,
  T extends MetroContextType[K],
>(obj: MetroContextType, prop: K, value: T) {
  if (prop === 'appName') {
    if (obj[prop] === 'modulelibrary') {
      obj[prop] = value;
    }

    return;
  }

  if (prop === 'customHandler') {
    if (obj[prop] === null) {
      obj[prop] = value;
    }

    return;
  }

  if (obj[prop] === 'unknown') {
    obj[prop] = value;
  }
}
